export interface ResolveMappingProfileAutoRepairGateInput {
  mappingReadinessEnabled: boolean;
  hasResolvedSelectedProfile: boolean;
  hasProjectProfileTrust: boolean;
  hasCurrentSessionProjectTrustExemption: boolean;
  profileConfirmedForCurrentSession: boolean;
  confirmedRepairProfileKey: string | null;
  resolvedProfileKey: string;
  resolvedProfileName: string;
  missingParameterPreview: string | null;
}

export interface MappingProfileAutoRepairGateResult {
  allowed: boolean;
  blockedMessage: string | null;
}

// Repair mutates the active Revit project, so it requires an explicit
// confirmation issued in the current session. The project trust record lives
// in localStorage and survives the flow that created it; deriving repair
// permission from it silently started repairs on every later page visit.
// `confirmedRepairProfileKey` is in-memory document-session state set only by
// an explicit action. The narrower trust exemption can be set only by the
// standard-parameters landing choice or an already-authorized wizard repair.
// Neither is restored, so no further handoff guard is needed here. Excluding
// the live wizard handoff used to deadlock exactly those explicit actions on
// the landing mount: the "Repair setup" button confirmed and nothing happened.
export function resolveMappingProfileAutoRepairGate({
  mappingReadinessEnabled,
  hasResolvedSelectedProfile,
  hasProjectProfileTrust,
  hasCurrentSessionProjectTrustExemption,
  profileConfirmedForCurrentSession,
  confirmedRepairProfileKey,
  resolvedProfileKey,
  resolvedProfileName,
  missingParameterPreview,
}: ResolveMappingProfileAutoRepairGateInput): MappingProfileAutoRepairGateResult {
  const allowed =
    mappingReadinessEnabled &&
    hasResolvedSelectedProfile &&
    (hasProjectProfileTrust || hasCurrentSessionProjectTrustExemption) &&
    profileConfirmedForCurrentSession &&
    confirmedRepairProfileKey === resolvedProfileKey;

  if (allowed) {
    return { allowed, blockedMessage: null };
  }

  const blockedMessage = !hasResolvedSelectedProfile
    ? 'Waiting for the mapping profile selection before automatic parameter setup runs.'
    : !profileConfirmedForCurrentSession
      ? `Confirm the selected mapping profile "${resolvedProfileName}" for this Revit project to enable automatic parameter setup.`
      : missingParameterPreview
        ? `Profile "${resolvedProfileName}" is confirmed. Missing parameters: ${missingParameterPreview}. Select Create missing Revit parameters to add them.`
        : `Profile "${resolvedProfileName}" is confirmed. Select Create missing Revit parameters if automatic parameter setup does not start.`;

  return { allowed, blockedMessage };
}
